import {FC, useEffect, useState, useMemo} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
	getCurrentEventSelector,
	getEventsLoadingSelector,
	getEventTodoList,
	getEventTotoListSelector,
} from '@/store/organization-event';
//mui
import {Badge, Box, Tab, Tabs, Typography} from '@mui/material';
//components
import TodoItem from '@/components/Organization/Dashboard/TodoList/TodoItem';
//types
import {IEventTodoList, IOrganizationEvent} from '@/utils/types';
//constants
import {BODY1, H6, SECONDARY, SUBTITLE2} from '@/utils/constants';
import {TODO_LIST} from '@constants/dashboard';
//helpers
import {getErrorMessage} from '@helpers/error';
//styles
import {todoListPageStyle} from './TodoListPage.styles';
import Loader from '@/shared/components/Loader';

export const createTodoData: Omit<IEventTodoList, 'id' | 'eventId'>[] = [
	{
		name: 'Review Event Settings',
		description: 'Check all the event settings to make sure everything is fine',
		isDone: false,
		templateKey: 'ReviewEventSettings',
	},
	{
		name: 'Add Sponsor Tiers And Logos',
		description: 'Add all event sponsors, upload their logos and set tiers',
		isDone: false,
		templateKey: 'AddSponsorTiers',
	},
	{
		name: 'Table And Ticket Settings',
		description: 'Set ticket tiers, seats per ticket, and total amount',
		isDone: false,
		templateKey: 'TableTicketSettings',
	},
	{
		name: 'Add Auction Items',
		description: 'Add auction items for your event',
		isDone: false,
		templateKey: 'AddAuctionItems',
	},
	{
		name: 'Customize Your Email Templates',
		description: 'Set general email settings',
		isDone: false,
		templateKey: 'CustomizeEmailTemplates',
	},
	{
		name: 'Customize Your Table/Ticket Purchase Page',
		description: 'Personalize the ticket purchase page',
		isDone: false,
		templateKey: 'CustomizeTicketPage',
	},
	{
		name: 'Review Invoice Email',
		description: 'Review the invoice email template',
		isDone: false,
		templateKey: 'ReviewInvoiceEmail',
	},
	{
		name: 'Review Acknowledgment Email',
		description: 'Review the acknowledgment email template',
		isDone: false,
		templateKey: 'ReviewAcknowledgmentEmail',
	},
	{
		name: 'Table Update Email',
		description: 'Update guests about their tables',
		isDone: false,
		templateKey: 'TableUpdateEmail',
	},
	{
		name: 'Patron Update Email',
		description: 'Update patrons about event details',
		isDone: false,
		templateKey: 'PatronUpdateEmail',
	},
	{
		name: 'Auction Email',
		description: 'Send auction-related updates',
		isDone: false,
		templateKey: 'AuctionEmail',
	},
	{
		name: 'Invoice Email',
		description: 'Send invoices to guests or donors',
		isDone: false,
		templateKey: 'InvoiceEmail',
	},
	{
		name: 'Email Notification Of Auction',
		description: 'Notify participants about auction',
		isDone: false,
		templateKey: 'NotifyAuctionStart',
	},
	{
		name: 'Print Bid Sheets',
		description: 'Print all bid sheets',
		isDone: false,
		templateKey: 'PrintBidSheets',
	},
];

const TodoListPage: FC = () => {
	const dispatch = useAppDispatch();

	const currentEvent: IOrganizationEvent | null = useAppSelector(getCurrentEventSelector);
	const todoList: IEventTodoList[] = useAppSelector(getEventTotoListSelector);
	const isLoading = useAppSelector(getEventsLoadingSelector);

	const [activeTab, setActiveTab] = useState('required');

	const listTodos = useMemo(() => (activeTab === 'required' ? todoList : createTodoData), [activeTab, todoList]);

	useEffect(() => {
		const getSponsorCategories = async () => {
			try {
				const reqBody = {
					id: currentEvent!.id,
					isDone: false,
				};

				await dispatch(getEventTodoList(reqBody)).unwrap();
			} catch (error) {
				toast.error(getErrorMessage(error));
			}
		};

		if (currentEvent) getSponsorCategories();
	}, [currentEvent]);

	const changeTab = (_: React.SyntheticEvent, newTab: string) => {
		setActiveTab(newTab);
	};

	return (
		<Box sx={todoListPageStyle.wrapper}>
			<Typography variant={H6}>{TODO_LIST}</Typography>
			<Typography sx={todoListPageStyle.description} variant={BODY1}>
				You have {todoList.length} actions on your to-do list that should be done for a successful event
			</Typography>

			<Box>
				<Tabs value={activeTab} onChange={changeTab} indicatorColor={SECONDARY} sx={todoListPageStyle.tabs}>
					<Tab
						disableRipple
						value="required"
						label={
							<Box>
								<Typography variant={SUBTITLE2}>
									Required actions
									{todoList.length > 0 && <Badge sx={todoListPageStyle.badgeIcon} badgeContent={todoList.length} />}
								</Typography>
							</Box>
						}
					/>
					<Tab
						disableRipple
						value="all"
						label={
							<Box>
								<Typography variant={SUBTITLE2}>All actions </Typography>
							</Box>
						}
					/>
				</Tabs>
			</Box>

			{isLoading ? (
				<Loader />
			) : (
				listTodos.map((todoItem) => <TodoItem todoItem={todoItem} key={todoItem.templateKey} />)
			)}
		</Box>
	);
};

export default TodoListPage;
